import { Fragment, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { BodyCell } from './Cells';
import { TableContentContainer } from './TableContentContainer';
import { DesktopTHead } from './desktop/DesktopTHead';
import { ReactComponent as ChevroneDown } from '../../assets/img/icons/common/ChevroneDown.svg';
import { ReactComponent as ChevroneUp } from '../../assets/img/icons/common/ChevroneUp.svg';
import useIsMobile from '../../hooks/useIsMobile';
import useStickyHeader from '../../hooks/useStickyHeader';

const initData = (rows) => {
  return rows.map((row) => ({
    ...row,
    isOpen: false,
  }));
};

export const HierarchicalTable = ({
  rows,
  headerOptions,
  innerHeaderOptions,
  InnerTableComponent,
  isLoading,
  emptyText,
  sortModel,
  className = '',
  stickyHeader,
}) => {
  const [tableData, setTableData] = useState(() => initData(rows));
  const tableRef = useRef(null);
  const isMobile = useIsMobile();
  const tableStickyHeader = !isMobile && rows.length ? stickyHeader : undefined;
  const lastRowIndex = rows.length - 1;
  const hasStickyHeader = !!tableStickyHeader;
  
  useStickyHeader({ stickyHeader: tableStickyHeader, tableRef, rows });

  const handleRowClick = (row) => {
    setTableData((prev) =>
      prev.map((item) => (item.id !== row.id ? item : { ...item, isOpen: !item.isOpen }))
    );
  };

  useEffect(() => {
    setTableData(initData(rows));
  }, [rows]);

  return (
    <div className={cx('custom-table__wrapper', { 'custom-table__wrapper-sticky': hasStickyHeader, [className]: !!className })}>
      <table className="custom-table__table hierarchical-table" ref={tableRef}>
        <DesktopTHead headerOptions={headerOptions} sortModel={sortModel} />
        <TableContentContainer rows={rows} isLoading={isLoading} emptyText={emptyText}>
          {tableData.map((row, rowIndex) => (
            <Fragment key={row.id}>
              <tr
                className={cx('custom-table__tbody-row', {
                  selectable: row.onSelectClick,
                  unclickable: !!row.unclickable,
                  'without-border': row.isOpen,
                })}
                style={{ height: row.minHeight || 'auto' }}
                onClick={() => !row.unclickable && handleRowClick(row)}
              >
                {row.cells.map((cell, index) => {
                  const rowCell = !cell.isArrow
                    ? cell
                    : {
                        ...cell,
                        cellComponent: row.isOpen ? <ChevroneUp /> : <ChevroneDown />,
                      };
                  return (
                    <BodyCell
                      key={`${row.id}-${index}`}
                      cell={rowCell}
                      stickyHeaderObserverAnchor={hasStickyHeader && lastRowIndex === rowIndex && index === 0}
                    />
                  );
                })}
              </tr>
              {row.isOpen &&
                (InnerTableComponent ? (
                  <tr>
                    <td colSpan={headerOptions.length}>{InnerTableComponent({ outerRow: row })}</td>
                  </tr>
                ) : (
                  <>
                    {innerHeaderOptions && row.items.length > 0 ? (
                      <tr className="custom-table__tbody-row subRow innerTHead" key={`inner-thead`}>
                        {innerHeaderOptions.map((innerHeaderCell, i) => (
                          <BodyCell key={i} cell={innerHeaderCell} />
                        ))}
                      </tr>
                    ) : null}
                    {row.items.map((subRow, i) => (
                      <tr
                        className={cx('custom-table__tbody-row subRow', {
                          'last-row': row.items.length - 1 === i,
                        })}
                        key={`${row.id}-items${i}`}
                      >
                        {subRow.cells.map((subRowCell, i) => (
                          <BodyCell key={`${row.id}-${i}-items`} cell={subRowCell} />
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
            </Fragment>
          ))}
        </TableContentContainer>
      </table>
    </div>
  );
};
